import React, { useEffect, useMemo, useState, useRef } from 'react';
import {
  Box,
  Button,
  Chip,
  Divider,
  IconButton,
  InputAdornment,
  Popover,
  Tab,
  Tabs,
  TextField,
  Typography,
  alpha
} from '@mui/material';
import { Check, Palette, RotateCcw, Search, Sparkles, Type } from 'lucide-react';
import {
  CSS_PRIMARY,
  DEFAULT_FONT_FAMILY,
  DEFAULT_FONT_SIZE,
  DEFAULT_PRIMARY_COLOR,
  DEFAULT_PRIMARY_COLOR_HOVER,
  FONT_FAMILY_PRESETS,
  getThemeColors,
  getThemeFontFamily,
  getThemeFontSize,
  normalizeFontFamily,
  setThemeColors,
  setThemeFontFamily,
  setThemeFontSize,
  THEME_COLORS_CHANGED_EVENT,
  THEME_FONT_CHANGED_EVENT,
  THEME_FONT_SIZE_CHANGED_EVENT
} from '../../../constants/style';

const COLOR_PRESETS = [
  { label: 'Teal', primary: '#089bab', hover: '#067d8b' },
  { label: 'Blue', primary: '#0854ab', hover: '#06548b' },
  { label: 'Green', primary: '#36c95f', hover: '#27ae60' },
  { label: 'Purple', primary: '#7356f1', hover: '#5a3fd4' },
  { label: 'Orange', primary: '#f59e0b', hover: '#d97706' },
  { label: 'Rose', primary: '#e11d48', hover: '#be123c' }
];

const toPickerValue = (hex) => (hex?.length === 9 ? hex.slice(0, 7) : hex);

const panelPaperSx = {
  width: 320,
  maxWidth: '92vw',
  borderRadius: 2.5,
  overflow: 'hidden',
  boxShadow: '0 16px 48px rgba(15, 23, 42, 0.18)',
  border: '1px solid',
  borderColor: alpha('#0f172a', 0.08)
};

const ThemeSettingsControl = () => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [tab, setTab] = useState(0);
  const [colors, setColors] = useState(getThemeColors);
  const [fontFamily, setFontFamily] = useState(getThemeFontFamily);
  const [fontSize, setFontSize] = useState(getThemeFontSize);
  const [fontSearch, setFontSearch] = useState('');
  const [customFont, setCustomFont] = useState('');
  const [customFontSize, setCustomFontSize] = useState('');

  const open = Boolean(anchorEl);

  useEffect(() => {
    const onColors = (e) => e.detail?.primary && setColors(e.detail);
    const onFont = (e) => {
      if (e.detail?.fontFamily) {
        setFontFamily(e.detail.fontFamily);
        setCustomFont(e.detail.fontFamily);
      }
    };
    const onFontSize = (e) => {
      if (e.detail?.fontSize) {
        setFontSize(e.detail.fontSize);
        setCustomFontSize(e.detail.fontSize.replace('px', ''));
      }
    };
    window.addEventListener(THEME_COLORS_CHANGED_EVENT, onColors);
    window.addEventListener(THEME_FONT_CHANGED_EVENT, onFont);
    window.addEventListener(THEME_FONT_SIZE_CHANGED_EVENT, onFontSize);
    return () => {
      window.removeEventListener(THEME_COLORS_CHANGED_EVENT, onColors);
      window.removeEventListener(THEME_FONT_CHANGED_EVENT, onFont);
      window.removeEventListener(THEME_FONT_SIZE_CHANGED_EVENT, onFontSize);
    };
  }, []);

  useEffect(() => {
    if (open) {
      setCustomFont(getThemeFontFamily());
      setCustomFontSize(getThemeFontSize().replace('px', ''));
    }
  }, [open]);

  const filteredFonts = useMemo(() => {
    const q = fontSearch.trim().toLowerCase();
    if (!q) return FONT_FAMILY_PRESETS;
    return FONT_FAMILY_PRESETS.filter(
      (f) =>
        f.label.toLowerCase().includes(q) ||
        f.value.toLowerCase().includes(q) ||
        (f.group && f.group.toLowerCase().includes(q))
    );
  }, [fontSearch]);

  const colorTimer = useRef(null);

  const applyColors = (primary, primaryHover) => {
    if (colorTimer.current) {
      clearTimeout(colorTimer.current);
    }
    colorTimer.current = setTimeout(() => {
      setColors(setThemeColors(primary, primaryHover));
      colorTimer.current = null;
    }, 500);
  };

  useEffect(() => {
    return () => {
      if (colorTimer.current) {
        clearTimeout(colorTimer.current);
        colorTimer.current = null;
      }
    };
  }, []);

  const applyFont = (family) => {
    const next = setThemeFontFamily(family);
    setFontFamily(next);
    setCustomFont(next);
  };

  const handleApplyCustomFont = () => {
    const normalized = normalizeFontFamily(customFont);
    if (normalized) applyFont(normalized);
  };

  const applyFontSize = (size) => {
    const next = setThemeFontSize(size);
    setFontSize(next);
    setCustomFontSize(next.replace('px', ''));
  };

  const handleApplyCustomFontSize = () => {
    applyFontSize(customFontSize);
  };

  return (
    <Box component="li" className="nav-item" sx={{ listStyle: 'none' }}>
      <IconButton
        type="button"
        title="Appearance settings"
        onClick={(e) => setAnchorEl(e.currentTarget)}
        sx={{
          width: 40,
          height: 40,
          borderRadius: 2,
          border: '1px solid',
          borderColor: alpha(colors.primary, 0.25),
          background: `linear-gradient(135deg, ${alpha(colors.primary, 0.12)} 0%, ${alpha(
            colors.primaryHover,
            0.18
          )} 100%)`,
          transition: 'transform 0.2s ease, box-shadow 0.2s ease',
          '&:hover': {
            transform: 'translateY(-1px)',
            boxShadow: `0 6px 16px ${alpha(colors.primary, 0.28)}`
          }
        }}
      >
        <Sparkles size={18} color={colors.primary} />
      </IconButton>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          paper: { sx: panelPaperSx }
        }}
      >
        <Box
          sx={{
            px: 2,
            py: 1.5,
            background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.primaryHover} 100%)`,
            color: '#fff'
          }}
        >
          <Typography
            variant="subtitle2"
            fontWeight={700}
            sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
          >
            <Sparkles size={16} />
            Appearance
          </Typography>
          <Typography variant="caption" sx={{ opacity: 0.9 }}>
            Customize brand colors & typography
          </Typography>
        </Box>

        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          variant="fullWidth"
          sx={{
            minHeight: 42,
            borderBottom: 1,
            borderColor: 'divider',
            '& .MuiTab-root': { minHeight: 42, fontSize: 12, fontWeight: 600, gap: 0.5 },
            '& .Mui-selected': { color: CSS_PRIMARY }
          }}
        >
          <Tab icon={<Palette size={14} />} iconPosition="start" label="Colors" />
          <Tab icon={<Type size={14} />} iconPosition="start" label="Fonts" />
        </Tabs>

        <Box sx={{ p: 2 }}>
          {tab === 0 && (
            <Box>
              <Box
                sx={{
                  height: 44,
                  borderRadius: 2,
                  mb: 2,
                  background: `linear-gradient(90deg, ${colors.primary} 0%, ${colors.primaryHover} 100%)`,
                  boxShadow: `0 4px 14px ${alpha(colors.primary, 0.35)}`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <Typography variant="caption" sx={{ color: '#fff', fontWeight: 600 }}>
                  Live preview
                </Typography>
              </Box>

              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5, mb: 2 }}>
                <Box>
                  <Typography variant="caption" color="text.secondary" fontWeight={600}>
                    Primary
                  </Typography>
                  <Box
                    sx={{
                      mt: 0.5,
                      p: 0.5,
                      borderRadius: 1.5,
                      border: '1px solid',
                      borderColor: 'divider',
                      bgcolor: alpha(colors.primary, 0.06)
                    }}
                  >
                    <input
                      type="color"
                      value={toPickerValue(colors.primary)}
                      onChange={(e) => applyColors(e.target.value, colors.primaryHover)}
                      style={{
                        width: '100%',
                        height: 36,
                        border: 'none',
                        cursor: 'pointer',
                        background: 'transparent'
                      }}
                    />
                  </Box>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary" fontWeight={600}>
                    Hover
                  </Typography>
                  <Box
                    sx={{
                      mt: 0.5,
                      p: 0.5,
                      borderRadius: 1.5,
                      border: '1px solid',
                      borderColor: 'divider',
                      bgcolor: alpha(colors.primaryHover, 0.06)
                    }}
                  >
                    <input
                      type="color"
                      value={toPickerValue(colors.primaryHover)}
                      onChange={(e) => applyColors(colors.primary, e.target.value)}
                      style={{
                        width: '100%',
                        height: 36,
                        border: 'none',
                        cursor: 'pointer',
                        background: 'transparent'
                      }}
                    />
                  </Box>
                </Box>
              </Box>

              <Typography
                variant="caption"
                color="text.secondary"
                fontWeight={600}
                sx={{ mb: 1, display: 'block' }}
              >
                Quick presets
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 2 }}>
                {COLOR_PRESETS.map((preset) => (
                  <Chip
                    key={preset.label}
                    size="small"
                    label={preset.label}
                    onClick={() => applyColors(preset.primary, preset.hover)}
                    sx={{
                      fontWeight: 600,
                      border:
                        colors.primary === preset.primary
                          ? `2px solid ${preset.primary}`
                          : '1px solid',
                      borderColor: colors.primary === preset.primary ? preset.primary : 'divider',
                      '&::before': {
                        content: '""',
                        width: 10,
                        height: 10,
                        borderRadius: '50%',
                        bgcolor: preset.primary,
                        mr: 0.5,
                        ml: 0.5
                      }
                    }}
                  />
                ))}
              </Box>

              <Button
                fullWidth
                size="small"
                variant="outlined"
                startIcon={<RotateCcw size={14} />}
                onClick={() => applyColors(DEFAULT_PRIMARY_COLOR, DEFAULT_PRIMARY_COLOR_HOVER)}
                sx={{ textTransform: 'none', borderRadius: 1.5 }}
              >
                Reset colors
              </Button>
            </Box>
          )}

          {tab === 1 && (
            <Box>
              <TextField
                size="small"
                fullWidth
                placeholder="Search fonts..."
                value={fontSearch}
                onChange={(e) => setFontSearch(e.target.value)}
                sx={{ mb: 1.5 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search size={16} />
                    </InputAdornment>
                  )
                }}
              />

              <Box
                sx={{
                  maxHeight: 160,
                  overflowY: 'auto',
                  overflowX: 'hidden',
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1.5,
                  mb: 1.5,
                  '&::-webkit-scrollbar': { width: 6 },
                  '&::-webkit-scrollbar-thumb': {
                    background: alpha('#64748b', 0.4),
                    borderRadius: 8
                  }
                }}
              >
                {filteredFonts.length === 0 ? (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ p: 1.5, display: 'block' }}
                  >
                    No fonts match your search.
                  </Typography>
                ) : (
                  filteredFonts.map((preset) => {
                    const selected = fontFamily === preset.value;
                    return (
                      <Box
                        key={preset.value}
                        onClick={() => applyFont(preset.value)}
                        sx={{
                          px: 1.5,
                          py: 1,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          cursor: 'pointer',
                          fontFamily: preset.value,
                          bgcolor: selected ? alpha(colors.primary, 0.1) : 'transparent',
                          borderLeft: selected
                            ? `3px solid ${colors.primary}`
                            : '3px solid transparent',
                          transition: 'background 0.15s ease',
                          '&:hover': { bgcolor: alpha(colors.primary, 0.06) }
                        }}
                      >
                        <Box>
                          <Typography
                            variant="body2"
                            fontWeight={selected ? 700 : 500}
                            sx={{ fontFamily: preset.value }}
                          >
                            {preset.label}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {preset.group}
                          </Typography>
                        </Box>
                        {selected && <Check size={16} color={colors.primary} />}
                      </Box>
                    );
                  })
                )}
              </Box>

              <Divider sx={{ my: 1.5 }}>
                <Typography variant="caption" color="text.secondary">
                  Custom font
                </Typography>
              </Divider>

              <Box sx={{ display: 'flex', gap: 1.5, mb: 1 }}>
                <TextField
                  size="small"
                  fullWidth
                  label="Font family"
                  placeholder='e.g. "Inter"'
                  value={customFont}
                  onChange={(e) => setCustomFont(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleApplyCustomFont()}
                  helperText="CSS font format"
                  InputProps={{ sx: { fontFamily: customFont || 'inherit' } }}
                />
                <TextField
                  size="small"
                  label="Size (px)"
                  placeholder="11"
                  value={customFontSize}
                  onChange={(e) => setCustomFontSize(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleApplyCustomFontSize()}
                  helperText="e.g. 11"
                  sx={{ width: 100, flexShrink: 0 }}
                />
              </Box>

              <Box
                sx={{
                  p: 1.25,
                  mb: 1.5,
                  borderRadius: 1.5,
                  bgcolor: alpha(colors.primary, 0.06),
                  border: '1px dashed',
                  borderColor: alpha(colors.primary, 0.3),
                  fontFamily: customFont || fontFamily,
                  fontSize: fontSize || '11px'
                }}
              >
                The quick brown fox jumps over the lazy dog.
              </Box>

              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  fullWidth
                  size="small"
                  variant="contained"
                  onClick={() => {
                    handleApplyCustomFont();
                    handleApplyCustomFontSize();
                  }}
                  disabled={!normalizeFontFamily(customFont)}
                  sx={{
                    textTransform: 'none',
                    borderRadius: 1.5,
                    bgcolor: colors.primary,
                    '&:hover': { bgcolor: colors.primaryHover }
                  }}
                >
                  Apply custom
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => {
                    applyFont(DEFAULT_FONT_FAMILY);
                    applyFontSize(DEFAULT_FONT_SIZE);
                  }}
                  sx={{ textTransform: 'none', borderRadius: 1.5, minWidth: 44, px: 1 }}
                  title="Reset to Default"
                >
                  <RotateCcw size={14} />
                </Button>
              </Box>
            </Box>
          )}
        </Box>
      </Popover>
    </Box>
  );
};

export default ThemeSettingsControl;
